import { useMutation } from 'convex/react';
import { api } from '../../../../convex/_generated/api'; // DEEP_IMPORT_JUSTIFIED: benefit-plan-tabs subdirectory requires four levels up to reach convex/_generated/
import { EditableJsonTable } from './EditableJsonTable';
import type {
  IBenefitPlan,
  IBenefitSection,
  IBenefitItem,
  IBenefitSpecificExclusion,
} from '@/@types/claims';
import type { IEditableJsonTableColumn } from '@/@types/insurance';

interface IBenefitSpecificExclTabProps {
  // LOCAL_TYPE_JUSTIFIED: component-specific props combining plan with enriched sections
  plan: IBenefitPlan & {
    sections: Array<IBenefitSection & { items: IBenefitItem[] }>;
  };
}

const BENEFIT_COLORS: Record<string, 'green' | 'blue' | 'orange' | 'red' | 'violet'> = {
  'Emergency treatment outside area of coverage': 'red',
  'Inpatient hospitalisation': 'orange',
  'Inpatient hospitalization': 'orange',
  'Outpatient / specialist consultations': 'blue',
  Maternity: 'violet',
  'Newborn care': 'green',
  'Mental health / psychiatric': 'orange',
  Physiotherapy: 'green',
  'Cancer / oncology': 'red',
  'Organ transplant': 'violet',
  Dialysis: 'blue',
  'Durable Medical Equipment': 'blue',
  'Home health care': 'green',
  'Prescription drugs': 'blue',
  'Dental treatment': 'green',
  Optical: 'green',
  'Emergency evacuation': 'violet',
  'Hearing aids': 'blue',
};

const COLUMNS: IEditableJsonTableColumn<IBenefitSpecificExclusion>[] = [
  { key: 'id', header: 'ID', width: '60px', readOnly: true },
  { key: 'benefitCategory', header: 'Benefit', width: '220px', badgeColorMap: BENEFIT_COLORS },
  { key: 'exclusionOrLimitation', header: 'Exclusion or Limitation' },
];

export function BenefitSpecificExclTab({ plan }: IBenefitSpecificExclTabProps) {
  const updateTabData = useMutation(api.insurance.benefitPlans.updateTabData);

  const rows: IBenefitSpecificExclusion[] = (plan.benefitSpecificExclusions ?? []).map((r, i) => ({
    ...r,
    id: r.id || `BSE${String(i + 1).padStart(3, '0')}`,
  }));

  async function handleSave(updated: IBenefitSpecificExclusion[]) {
    await updateTabData({ planId: plan._id, field: 'benefitSpecificExclusions', data: updated });
  }

  return (
    <div className="py-6 px-8">
      <EditableJsonTable
        rows={rows}
        columns={COLUMNS}
        idPrefix="BSE"
        onSave={handleSave}
        emptyMessage="No benefit-specific exclusions defined."
      />
    </div>
  );
}
